import React, { useEffect, useState, useMemo } from "react";
import PropTypes from 'prop-types';
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { ChartBaseProps } from '../types/chart-types';
import { t } from "i18next";
import LivisResponsiveWrapper from "../wrappers/LivisResponsiveWrapper";

interface LivisGaugeSegmentedChartProps extends ChartBaseProps {
  height?: number;
  value?: string; // The percentage value (0-100)
  primaryColor?: string; // Color for the filled segments
  backgroundColor?: string; // Color for the empty segments
  innerRadius?: string | number;
  outerRadius?: string | number;
  startAngle?: number;
  endAngle?: number;
  totalSegments?: number; // Number of segments in the gauge
  paddingAngle?: number; // Gap between segments
  cornerRadius?: number; // Rounded edges for segments
  t?: (key: string) => string; // Translation function
  noDataMessage?: string;
}

const LivisGaugeSegmentedChart: React.FC<LivisGaugeSegmentedChartProps> = ({
  data,
  title,
  subtitle,
  tooltip = false,
  legend = false,
  onClickPoint,
  height = 300,
  value,
  primaryColor = "#007bff",
  backgroundColor = "#e9ecef",
  innerRadius = "55%",
  outerRadius = "100%",
  startAngle = 180,
  endAngle = 0,
  totalSegments = 20,
  paddingAngle = 4,
  cornerRadius = 3.5,
  name,
  noDataMessage = `${t("No Record Found")}`,
}) => {
  const [chartData, setChartData] = useState<number>(0);
  const [segmentData, setSegmentData] = useState<any[]>([]);

  // Extract value from data or use direct value prop
  const extractedValue = useMemo(() => {
    console.log("LivisGaugeSegmentedChart - data:", data);
    console.log("LivisGaugeSegmentedChart - value prop:", value);

    // Helper function to extract numeric value from string
    const extractNumericValue = (val: any) => {
      if (typeof val === 'number') {
        return val;
      }
      if (typeof val === 'string') {
        // Extract number from strings like "25°C", "75%", "30GB", etc.
        const numericMatch = val.match(/[\d.]+/);
        return numericMatch ? Number(numericMatch[0]) : 0;
      }
      return Number(val) || 0;
    };

    // Priority: value prop > data prop > 0
    if (value !== undefined && value !== null) {
      return extractNumericValue(value);
    }

    if (data !== undefined && data !== null) {
      // Handle array format
      if (Array.isArray(data) && data.length > 0) {
        const extractedVal = data[0]?.value;
        console.log("Extracted value from array:", extractedVal);
        return extractNumericValue(extractedVal);
      }
      // Handle direct number
      if (typeof data === 'number') {
        return Number(data);
      }
    }

    return 0;
  }, [data, value]);

  // Update chart data when value changes
  useEffect(() => {
    console.log("Setting chartData to:", extractedValue);
    setChartData(extractedValue);
  }, [extractedValue]);

  // Create segment data for the gauge
  useEffect(() => {
    console.log("Creating segments for chartData:", chartData);
    const filledSegments = Math.floor((chartData / 100) * totalSegments);
    console.log("Filled segments:", filledSegments, "out of", totalSegments);

    const segments = Array.from({ length: totalSegments }, (_, i) => ({
      name: `Segment-${i}`,
      value: 1,
      filled: i < filledSegments,
    }));

    setSegmentData(segments);
  }, [chartData, totalSegments]);

  // Handle segment click
  const handleSegmentClick = (clickData: any, index: number) => {
    if (onClickPoint) {
      onClickPoint({
        x: index,
        y: clickData.value,
        name: clickData.name,
        series: {
          name: name || 'GaugeSegmented',
          type: 'gauge-segmented'
        },
        point: clickData
      });
    }
  };

  // No data state
  if (chartData === undefined || chartData === null || (Array.isArray(data) && data.length === 0)) {
    console.log("No data condition triggered");
    return (
      <div style={{
        height: `${height}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column'
      }}>
        {title && <h3 style={{ margin: '0 0 8px 0' }}>{title}</h3>}
        <p style={{ textAlign: "center" }}>{t("No data available for selected filters")}</p>
      </div>
    );
  }

  console.log("Rendering chart with segmentData:", segmentData);
  console.log("Chart will render with height:", height);

  return (
    <>
      <div className="livis-reports-analytics-gauge-container" style={{ height: `${height}px`, width: '100%', position: 'relative' }}>
        <LivisResponsiveWrapper height={height * 1.5} style={{ position: 'relative', top: -120 }}>
          <PieChart>
            <Pie
              data={segmentData}
              dataKey="value"
              cx="50%"
              cy="100%"
              startAngle={startAngle}
              endAngle={endAngle}
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              paddingAngle={paddingAngle}
              cornerRadius={cornerRadius}
              onClick={(data, index) => handleSegmentClick(data, index)}
              cursor={onClickPoint ? "pointer" : "default"}
            >
              {segmentData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.filled ? primaryColor : backgroundColor}
                  stroke="none"
                />
              ))}
            </Pie>
          </PieChart>
        </LivisResponsiveWrapper>

        <div className="livis-reports-analytics-gauge-center">
          <div className="livis-reports-analytics-gauge-value">
            {/* Display original string value if available, otherwise show numeric value */}
            {(() => {
              // Helper function to format number and remove trailing zeros
              const formatNumber = (num: number) => {
                return parseFloat(num.toFixed(2)).toString();
              };

              // Priority: value prop > data[0].value > chartData
              if (value !== undefined && value !== null) {
                // If value is a string with numbers, format it
                if (typeof value === 'string') {
                  const numericMatch = value.match(/[\d.]+/);
                  if (numericMatch) {
                    const numericPart = Number(numericMatch[0]);
                    const nonNumericPart = value.replace(/[\d.]+/, '');
                    return `${formatNumber(numericPart)}${nonNumericPart}`;
                  }
                }
                return value;
              }
              if (data && Array.isArray(data) && data.length > 0 && data[0]?.value !== undefined) {
                const dataValue = data[0].value;
                // Format numeric values to 2 decimal places and remove trailing zeros
                if (typeof dataValue === 'number') {
                  return formatNumber(dataValue);
                }
                if (typeof dataValue === 'string') {
                  const numericMatch = dataValue.match(/[\d.]+/);
                  if (numericMatch) {
                    const numericPart = Number(numericMatch[0]);
                    const nonNumericPart = dataValue.replace(/[\d.]+/, '');
                    return `${formatNumber(numericPart)}${nonNumericPart}`;
                  }
                }
                return dataValue;
              }
              // Format chartData to 2 decimal places and remove trailing zeros
              return formatNumber(chartData);
            })()}
          </div>
          {/* <div className="livis-reports-analytics-gauge-label">
            This month
          </div> */}
        </div>
      </div>
    </>
  );
};

// PropTypes validation
LivisGaugeSegmentedChart.propTypes = {
  data: PropTypes.any,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  tooltip: PropTypes.bool,
  legend: PropTypes.bool,
  onClickPoint: PropTypes.func,
  height: PropTypes.number,
  value: PropTypes.string,
  primaryColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  innerRadius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  outerRadius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  startAngle: PropTypes.number,
  endAngle: PropTypes.number,
  totalSegments: PropTypes.number,
  paddingAngle: PropTypes.number,
  cornerRadius: PropTypes.number,
  name: PropTypes.string,
  t: PropTypes.func,
};

// Default props
LivisGaugeSegmentedChart.defaultProps = {
  data: [],
  title: '',
  subtitle: '',
  tooltip: false,
  legend: false,
  onClickPoint: undefined,
  height: 300,
  value: undefined,
  primaryColor: "#007bff",
  backgroundColor: "#e9ecef",
  innerRadius: "55%",
  outerRadius: "100%",
  startAngle: 180,
  endAngle: 0,
  totalSegments: 20,
  paddingAngle: 2,
  cornerRadius: 8,
  name: '',
  t: (key: string) => key,
};

export default LivisGaugeSegmentedChart;